import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import LoggerService from "@bfx/logger/dist";
import { MenuItem } from "@bfx/primeng/api";
import { SplitButton } from "@bfx/primeng/splitbutton";
import { TranslateService } from "@ngx-translate/core";
import { BmarkSummaryRiskFilterType } from "app/bfx-bmark-summary/models/bmark-summary-risk-filter-type.enum";

const logger = LoggerService.for(__dirname);

/**
 * Filter selector dropdown component for Fwd Risk blotter
 */
@Component({
  selector: "bfx-risk-filter",
  templateUrl: "./html/bfx-risk-filter.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxBlotterRiskFilterComponent {
  public disabled: boolean = false;

  @ViewChild("splitOptions") private splitOptions: SplitButton;

  /** All menu items */
  public items: Array<MenuItem | null> = [];

  /** Internal map of filter type to menu item */
  private menuMap = new Map<BmarkSummaryRiskFilterType, MenuItem>();

  /** Signal for current label */
  public activeMenuLabel: WritableSignal<string> = signal(null);
  /** Signal for current icon */
  public activeMenuIcon: WritableSignal<string> = signal(null);

  /** Currently displayed value  */
  private _activeFilter = BmarkSummaryRiskFilterType.ALL;

  /** Event emitted on filter change */
  @Output() activeFilterChange = new EventEmitter<BmarkSummaryRiskFilterType>();

  constructor(private translate: TranslateService) {
    this.initialiseData();
  }

  private initialiseData() {
    const allItem: MenuItem = {
      label: this.translate.instant("bmark_summary.fwd_risk.all"),
      icon: "fa icon-dot cold",
      command: () => {
        this.activeFilter = BmarkSummaryRiskFilterType.ALL;
      },
    };
    const warnItem = {
      label: this.translate.instant("bmark_summary.fwd_risk.warn"),
      icon: "fa icon-dot warm",
      command: () => {
        this.activeFilter = BmarkSummaryRiskFilterType.WARNING;
      },
    };
    const alertItem = {
      label: this.translate.instant("bmark_summary.fwd_risk.alert"),
      icon: "fa icon-dot hot",
      command: () => {
        this.activeFilter = BmarkSummaryRiskFilterType.ALERT;
      },
    };

    this.menuMap.set(BmarkSummaryRiskFilterType.ALL, allItem);
    this.menuMap.set(BmarkSummaryRiskFilterType.WARNING, warnItem);
    this.menuMap.set(BmarkSummaryRiskFilterType.ALERT, alertItem);
    this.items = [allItem, warnItem, alertItem];

    this.updateSignals();
  }

  /**
   * Public setter for current active filter - if value has changed, emits an activeFilterChange event
   */
  @Input()
  public set activeFilter(value: BmarkSummaryRiskFilterType) {
    if (value != this._activeFilter) {
      this._activeFilter = value;
      this.updateSignals();
      this.activeFilterChange.emit(this._activeFilter);
    }
  }

  /**
   * Update internal signals for the view
   */
  protected updateSignals() {
    const item = this.menuMap.get(this._activeFilter);
    this.activeMenuLabel.set(item?.label);
    this.activeMenuIcon.set(item?.icon);
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  showMenu(): void {
    if (this.splitOptions) {
      this.splitOptions.dropdownClick = true;
      this.splitOptions.overlayVisible = !this.splitOptions.overlayVisible;
    }
  }
}
